/**
 * The first-run tour, owed to a new account that has not had it yet.
 *
 * WHY THIS IS STORED
 *   A new account is greeted with the tour once the things in front of the app
 *   are gone: the legal splash, and (since 2026-10-02) the trainer step. While
 *   one of them is up, SessionProvider remembers that the tour is owed. That
 *   memory was a ref, and it has to outlive the page: the trainer step says
 *   "type them as they appear in Pokémon GO", so a new trainer switches to GO
 *   to check their name, iOS evicts the WebView behind it, and they come back
 *   to a cold start. The step reopens (its request is in localStorage) but the
 *   account is past the 60 second "new account" window by then, and a ref
 *   would have forgotten the tour for good.
 *
 * Deliberately NOT inferred from the trainer-step request: Live Scan and the
 * Friends QR sheet request that step for accounts of any age, and those have
 * either had the tour or skipped it.
 *
 * Keyed by account id, like lib/profileSetupRequest, so on a shared device one
 * trainer's owed tour never opens for another.
 */

export const PENDING_TOUR_KEY = "nexus.pendingTour.v1";

function read(): number[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_TOUR_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === "number") : [];
  } catch {
    return [];
  }
}

function write(ids: number[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (ids.length === 0) localStorage.removeItem(PENDING_TOUR_KEY);
    else localStorage.setItem(PENDING_TOUR_KEY, JSON.stringify(ids));
  } catch {
    /* quota or blocked storage: worst case the tour is not shown after a reboot */
  }
}

/** Is the tour still owed to this account? */
export function isTourPending(accountId: number | null | undefined): boolean {
  return accountId != null && read().includes(accountId);
}

/** A new account was seen behind the legal splash or the trainer step. */
export function markTourPending(accountId: number | null | undefined): void {
  if (accountId == null) return;
  const ids = read();
  if (!ids.includes(accountId)) write([...ids, accountId]);
}

/** The tour opened (or the account is gone): nothing is owed any more. */
export function clearTourPending(accountId: number | null | undefined): void {
  if (accountId == null) return;
  const ids = read();
  if (ids.includes(accountId)) write(ids.filter((id) => id !== accountId));
}
